import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { productsAPI, reviewsAPI, pagesAPI, mediaAPI, clicksAPI } from '../../lib/api';
import { Package, Star, FileText, Image, TrendingUp, Eye, MousePointerClick, BarChart3, Calendar } from 'lucide-react';

export const AdminDashboard = () => {
  const [stats, setStats] = useState({ products: 0, reviews: 0, pages: 0, media: 0 });
  const [clickStats, setClickStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [products, reviews, pages, media] = await Promise.all([
          productsAPI.getAll(), reviewsAPI.getAll(), pagesAPI.getAll(), mediaAPI.getAll(),
        ]);
        setStats({ products: products.data.length, reviews: reviews.data.length, pages: pages.data.length, media: media.data.length });
        try { const clicks = await clicksAPI.getStats(); setClickStats(clicks.data); } catch (e) {}
      } catch (e) { console.error('Failed:', e); }
      finally { setLoading(false); }
    };
    fetchStats();
  }, []);

  const statCards = [
    { icon: Package, label: 'Products', value: stats.products, color: 'bg-blue-500' },
    { icon: Star, label: 'Reviews', value: stats.reviews, color: 'bg-yellow-500' },
    { icon: FileText, label: 'Pages', value: stats.pages, color: 'bg-green-500' },
    { icon: Image, label: 'Media Files', value: stats.media, color: 'bg-purple-500' },
  ];
  const maxDaily = clickStats?.daily?.length > 0 ? Math.max(...clickStats.daily.map(d => d.count)) : 0;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Dashboard</h1>
        <p className="mt-2 text-slate-600">Overview of your sales page content</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <Card key={stat.label} className="overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">{stat.label}</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{loading ? '...' : stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}><stat.icon className="w-6 h-6 text-white" /></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {clickStats && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
            <MousePointerClick className="w-5 h-5 text-red-500" /> Click Tracking
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-slate-600">Total Clicks</p><p className="mt-2 text-3xl font-bold text-slate-900">{clickStats.total}</p></div><div className="bg-red-500 p-3 rounded-lg"><MousePointerClick className="w-6 h-6 text-white" /></div></div></CardContent></Card>
            <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-slate-600">Today</p><p className="mt-2 text-3xl font-bold text-slate-900">{clickStats.today}</p></div><div className="bg-orange-500 p-3 rounded-lg"><Calendar className="w-6 h-6 text-white" /></div></div></CardContent></Card>
            <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-slate-600">Last 30 Days</p><p className="mt-2 text-3xl font-bold text-slate-900">{clickStats.daily?.reduce((sum, d) => sum + d.count, 0) || 0}</p></div><div className="bg-indigo-500 p-3 rounded-lg"><BarChart3 className="w-6 h-6 text-white" /></div></div></CardContent></Card>
          </div>
          {clickStats.by_product?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Clicks by Product</CardTitle><CardDescription>Which packages get the most clicks</CardDescription></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {clickStats.by_product.map((p) => {
                    const pct = clickStats.total > 0 ? (p.count / clickStats.total) * 100 : 0;
                    return (
                      <div key={p.bottles}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-slate-700">{p.bottles} Bottle{p.bottles > 1 ? 's' : ''} - {p.product_name}</span>
                          <span className="text-sm font-bold text-slate-900">{p.count} clicks</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3">
                          <div className="h-3 rounded-full" style={{ width: pct + '%', background: p.bottles === 6 ? '#dc2626' : p.bottles === 3 ? '#1e3a8a' : '#64748b' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
          {clickStats.daily?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Daily Clicks (Last 30 Days)</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-end gap-1 h-40">
                  {clickStats.daily.map((d) => (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group relative">
                      <div className="absolute -top-8 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">{d.date}: {d.count}</div>
                      <div className="w-full bg-blue-500 rounded-t hover:bg-blue-600 cursor-pointer" style={{ height: maxDaily > 0 ? ((d.count / maxDaily) * 100) + '%' : '0%', minHeight: d.count > 0 ? '4px' : '0px' }} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {clickStats.total === 0 && (
            <Card><CardContent className="p-8 text-center text-slate-500"><MousePointerClick className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="font-medium">No clicks recorded yet</p></CardContent></Card>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-green-600" />Quick Tips</CardTitle></CardHeader>
          <CardContent><ul className="space-y-3 text-sm text-slate-600"><li>Update stock count for urgency</li><li>Add customer reviews for trust</li><li>Optimize SEO settings</li></ul></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-blue-600" />Getting Started</CardTitle></CardHeader>
          <CardContent><ul className="space-y-3 text-sm text-slate-600"><li><strong>Site Settings:</strong> Colors, promo bar, branding</li><li><strong>Products:</strong> Pricing and affiliate links</li><li><strong>SEO:</strong> Meta tags and structured data</li><li><strong>Navigation:</strong> Header and footer links</li></ul></CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
